import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';
import { useThemeStore } from '../../stores/themeStore';

interface ThemeToggleProps {
  showLabel?: boolean;
  className?: string;
}

export function ThemeToggle({ showLabel = false, className = '' }: ThemeToggleProps) {
  const { theme, setTheme } = useThemeStore();

  const options = [
    { value: 'light' as const, icon: SunIcon, label: 'Light' },
    { value: 'dark' as const, icon: MoonIcon, label: 'Dark' },
    { value: 'system' as const, icon: ComputerDesktopIcon, label: 'System' },
  ];

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {options.map((option) => {
        const Icon = option.icon;
        const isActive = theme === option.value;

        return (
          <button
            key={option.value}
            onClick={() => setTheme(option.value)}
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg
              transition-all duration-150
              focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500
              ${
                isActive
                  ? 'bg-surface-200 dark:bg-surface-700 text-surface-900 dark:text-surface-100'
                  : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800'
              }
            `}
            title={option.label}
          >
            <Icon className="w-5 h-5" />
            {showLabel && <span className="text-sm font-medium">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Simple toggle between light and dark (ignores system)
export function ThemeToggleSimple({ className = '' }: { className?: string }) {
  const { resolvedTheme, setTheme } = useThemeStore();

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  return (
    <button
      onClick={toggleTheme}
      className={`
        p-2 rounded-lg
        text-surface-500 hover:text-surface-700 dark:hover:text-surface-300
        hover:bg-surface-100 dark:hover:bg-surface-800
        transition-all duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500
        ${className}
      `}
      title={`Switch to ${resolvedTheme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {resolvedTheme === 'dark' ? (
        <SunIcon className="w-5 h-5" />
      ) : (
        <MoonIcon className="w-5 h-5" />
      )}
    </button>
  );
}

export default ThemeToggle;
